import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

interface SubscriptionContextType {
  isPremium: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  adQuota: { completed: number; required: number } | null;
  refreshAdQuota: () => Promise<void>;
  linkCount: number;
  linkLimit: number;
  canCreateLink: boolean;
  refreshLinkCount: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  isPremium: false,
  productId: null,
  subscriptionEnd: null,
  loading: true,
  checkSubscription: async () => {},
  adQuota: null,
  refreshAdQuota: async () => {},
  linkCount: 0,
  linkLimit: 5,
  canCreateLink: true,
  refreshLinkCount: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [isPremium, setIsPremium] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [adQuota, setAdQuota] = useState<{ completed: number; required: number } | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [linkCount, setLinkCount] = useState(0);
  
  const FREE_LINK_LIMIT = 5;
  const linkLimit = isPremium ? Infinity : FREE_LINK_LIMIT;
  const canCreateLink = isPremium || linkCount < FREE_LINK_LIMIT;

  const checkSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsPremium(false);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) throw error;
      
      setIsPremium(data.subscribed);
      setProductId(data.product_id);
      setSubscriptionEnd(data.subscription_end);
    } catch (error) {
      console.error("Error checking subscription:", error);
      setIsPremium(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshAdQuota = async () => {
    if (!user || isPremium) return;

    try {
      // 1) Ensure the current week's cycle exists and get its id
      const { data: cycleId, error: cycleError } = await supabase
        .rpc('get_or_create_current_cycle', { p_user_id: user.id });

      if (cycleError) throw cycleError;

      // 2) Read the cycle counts directly by id (guaranteed to exist)
      const { data, error } = await supabase
        .from('ad_cycles')
        .select('completed_count, required_count')
        .eq('id', cycleId)
        .single();

      if (error) throw error;

      setAdQuota({
        completed: data.completed_count,
        required: data.required_count,
      });
    } catch (error) {
      console.error("Error fetching ad quota:", error);
      setAdQuota({ completed: 0, required: 3 });
    }
  };

  const refreshLinkCount = async () => {
    if (!user) return;
    
    const { count, error } = await supabase
      .from('links')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);
    
    if (!error && count !== null) {
      setLinkCount(count);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session) {
        checkSubscription();
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session) {
        checkSubscription();
      } else {
        setIsPremium(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      if (!isPremium) {
        refreshAdQuota();
      }
      // Fetch link count
      const fetchLinkCount = async () => {
        const { count, error } = await supabase
          .from('links')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        
        if (!error && count !== null) {
          setLinkCount(count);
        }
      };
      fetchLinkCount();
    }
  }, [user, isPremium]);

  return (
    <SubscriptionContext.Provider
      value={{
        isPremium,
        productId,
        subscriptionEnd,
        loading,
        checkSubscription,
        adQuota,
        refreshAdQuota,
        linkCount,
        linkLimit,
        canCreateLink,
        refreshLinkCount,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
